import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibCheckbox } from '@chewy/kib-choices-react'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { useClickOutside } from '@chewy/kib-helpers-react'
import { KibEmergencyVetSimpleIcon, KibIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'
import { type Nil, useFields } from '@pbt/pbt-ui-components'

import { useIncludeMedicalRecord } from '~/store/hooks/conversations'

import '@chewy/kib-choices-styles/dist/styles/kib-checkbox.css'
import '@chewy/kib-patterns/dist/styles/kib-icon.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './MedicalLabel.module.scss'

const MedicalLabel = ({
  isMedical,
  isReadOnly,
  isDraft,
}: {
  isDraft: boolean
  isMedical: boolean | Nil
  isReadOnly: boolean
}) => {
  const buttonRef = useRef(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const { t } = useTranslation(['Communications'])
  const { shouldShowCheckbox } = useIncludeMedicalRecord()
  const canEdit = !isReadOnly && !isDraft && shouldShowCheckbox
  const {
    fields: { isMedical: isMedicalField },
  } = useFields([
    {
      name: 'isMedical',
      label: t('Communications:MEDICAL_RECORDS.INCLUDE_MESSAGE'),
      initialValue: isMedical ?? false,
    },
  ])
  const buttonIcon = isMedicalField.value && <KibEmergencyVetSimpleIcon />
  const buttonText = isMedicalField.value
    ? t('Communications:MESSAGE_CARD.MEDICAL_MESSAGE')
    : t('Communications:MESSAGE_CARD.ADMIN_MESSAGE')

  useClickOutside(buttonRef, () => {
    setMenuOpen(false)
  })

  useEffect(() => {
    const nonNullIsMedical = isMedical ?? false
    if (nonNullIsMedical !== isMedicalField.value) {
      isMedicalField.setValue(nonNullIsMedical)
    }
  }, [isMedical])

  if (!canEdit) {
    return (
      <div className={styles.plainTextLabel}>
        {buttonIcon && (
          <KibIcon className={styles.medicalIcon}>{buttonIcon}</KibIcon>
        )}
        {isMedicalField.value
          ? t('Communications:MESSAGE_CARD.MEDICAL_MESSAGE')
          : t('Communications:MESSAGE_CARD.ADMIN_MESSAGE')}
      </div>
    )
  }

  const handleChange = () => {
    isMedicalField.setValue(!isMedicalField.value)
  }

  return (
    <div
      className={classNames(styles.menuWrapper, {
        [styles.menuOpen]: menuOpen,
      })}
      ref={buttonRef}
    >
      <KibButtonNew
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        className={styles.menuTrigger}
        emphasis="tertiary"
        icon={buttonIcon}
        iconPosition="start"
        size="small"
        theme="utility"
        onClick={() => setMenuOpen(!menuOpen)}
      >
        {buttonText}
      </KibButtonNew>
      <div className={styles.menu} role="menu">
        <KibCheckbox
          aria-checked={isMedicalField.value}
          className={styles.checkbox}
          label={isMedicalField.label}
          role="menuitemcheckbox"
          selectedValue={isMedicalField.value}
          onChange={handleChange}
        />
      </div>
    </div>
  )
}

export default MedicalLabel
